class PopupBuilder extends HTMLElement {
  constructor() {
    super();
    this.data = null;
    this.eligible = [];
    this.queue = [];
    this.activePopup = null;
    this.activeLayer = null;
    this.hasExitIntentBound = false;
    this.sessionSeen = new Set();
    this.cookiesPrefix = 'pb_';
    this.storageFallback = {};
    this.triggerListeners = [];
  }

  connectedCallback() {
    const dataEl = this.querySelector('.popup-builder__data');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Popup Builder JSON parse error', e);
      return;
    }

    this.mount = this.querySelector('.popup-builder__mount');
    if (!this.mount) return;

    this.markVisitorState();
    this.eligible = this.getEligiblePopups();
    this.initTriggers();
  }

  disconnectedCallback() {
    this.cleanupListeners();
  }

  log(...args) {
    if (this.data?.debug) console.log('[PopupBuilder]', ...args);
  }

  cleanupListeners() {
    this.triggerListeners.forEach((fn) => fn && fn());
    this.triggerListeners = [];
  }

  parseLines(value) {
    return String(value || '')
      .split('\n')
      .map((x) => x.trim())
      .filter(Boolean);
  }

  getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  setCookie(name, value, days) {
    const expires = days
      ? '; expires=' + new Date(Date.now() + days * 86400000).toUTCString()
      : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/${expires}; SameSite=Lax`;
  }

  now() {
    return new Date();
  }

  getDeviceType() {
    const w = window.innerWidth;
    if (w < 750) return 'mobile';
    if (w < 990) return 'tablet';
    return 'desktop';
  }

  normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  arrayIncludesNormalized(list, value) {
    const v = this.normalize(value);
    return list.map((x) => this.normalize(x)).includes(v);
  }

  anyTagMatch(tagsInput, productTags) {
    const wanted = this.parseLines(tagsInput).map((x) => this.normalize(x));
    if (!wanted.length) return true;
    const current = (productTags || []).map((x) => this.normalize(x));
    return wanted.some((tag) => current.includes(tag));
  }

  pageScopeMatches(scope, page) {
    if (scope === 'all') return true;
    const type = this.normalize(page.type);

    if (scope === 'home') return type === 'index';
    if (scope === 'product') return type === 'product';
    if (scope === 'collection') return type === 'collection';
    if (scope === 'cart') return type === 'cart';
    if (scope === 'blog') return type === 'blog';
    if (scope === 'article') return type === 'article';
    if (scope === 'search') return type === 'search';
    if (scope === 'page') return type === 'page';
    if (scope === 'custom') return true;

    return false;
  }

  pathMatches(path, rules) {
    const p = this.normalize(path);
    return rules.some((rule) => p.includes(this.normalize(rule)));
  }

  scheduleMatches(schedule) {
    const now = this.now();
    const datePart = now.toISOString().slice(0, 10);
    const timePart = now.toTimeString().slice(0, 5);

    if (schedule.startDate && datePart < schedule.startDate) return false;
    if (schedule.endDate && datePart > schedule.endDate) return false;
    if (schedule.startTime && timePart < schedule.startTime) return false;
    if (schedule.endTime && timePart > schedule.endTime) return false;

    return true;
  }

  deviceMatches(deviceTarget) {
    const device = this.getDeviceType();
    if (deviceTarget === 'all') return true;
    return device === deviceTarget;
  }

  customerMatches(target) {
    const loggedIn = !!this.data.page.customerLoggedIn;
    const returning = this.isReturningVisitor();
    const newVisitor = !returning;

    if (target === 'all') return true;
    if (target === 'logged-in') return loggedIn;
    if (target === 'guests') return !loggedIn;
    if (target === 'new') return newVisitor;
    if (target === 'returning') return returning;
    return true;
  }

  triggerAudienceMatches(trigger, audience) {
    if (trigger.firstVisitOnly && this.isReturningVisitor()) return false;
    if (trigger.returningOnly && !this.isReturningVisitor()) return false;
    return this.customerMatches(audience.customerTarget);
  }

  isReturningVisitor() {
    return this.getCookie(this.cookiesPrefix + 'returning') === '1';
  }

  markVisitorState() {
    if (!this.isReturningVisitor()) {
      this.setCookie(this.cookiesPrefix + 'returning', '1', 365);
    }
  }

  getPopupCookieKey(popup, suffix) {
    return `${this.cookiesPrefix}${popup.id}_${suffix}`;
  }

  getViews(popup) {
    return parseInt(this.getCookie(this.getPopupCookieKey(popup, 'views')) || '0', 10) || 0;
  }

  setViews(popup, value) {
    this.setCookie(this.getPopupCookieKey(popup, 'views'), String(value), 365);
  }

  incrementViews(popup) {
    this.setViews(popup, this.getViews(popup) + 1);
  }

  getClosedAt(popup) {
    return this.getCookie(this.getPopupCookieKey(popup, 'closed_at'));
  }

  setClosedAt(popup) {
    this.setCookie(this.getPopupCookieKey(popup, 'closed_at'), String(Date.now()), 365);
  }

  isClosedForever(popup) {
    return this.getCookie(this.getPopupCookieKey(popup, 'closed_forever')) === '1';
  }

  setClosedForever(popup) {
    this.setCookie(this.getPopupCookieKey(popup, 'closed_forever'), '1', 3650);
  }

  shouldRespectFrequency(popup) {
    const freq = popup.frequency || {};
    const maxViews = parseInt(freq.maxViews || 0, 10);

    if (popup.frequency?.closeForever && this.isClosedForever(popup)) return false;
    if (maxViews > 0 && this.getViews(popup) >= maxViews) return false;

    const mode = freq.mode || 'always';
    const closedAt = parseInt(this.getClosedAt(popup) || '0', 10);

    if (!closedAt) return true;

    const elapsedDays = (Date.now() - closedAt) / 86400000;

    if (mode === 'always') return true;
    if (mode === 'session') return !this.sessionSeen.has(popup.id);
    if (mode === 'day') return elapsedDays >= 1;
    if (mode === 'week') return elapsedDays >= 7;
    if (mode === 'custom') return elapsedDays >= (parseInt(freq.customDays || 1, 10) || 1);

    return true;
  }

  visibilityMatches(popup) {
    const page = this.data.page;
    const vis = popup.visibility || {};

    if (!this.pageScopeMatches(vis.pageScope, page)) return false;

    const includeUrls = this.parseLines(vis.includeUrls);
    const excludeUrls = this.parseLines(vis.excludeUrls);
    const productHandles = this.parseLines(vis.productHandles);
    const excludeProductHandles = this.parseLines(vis.excludeProductHandles);
    const collectionHandles = this.parseLines(vis.collectionHandles);
    const excludeCollectionHandles = this.parseLines(vis.excludeCollectionHandles);
    const vendors = this.parseLines(vis.vendors);
    const productTypes = this.parseLines(vis.productTypes);
    const templates = this.parseLines(vis.templates);

    if (includeUrls.length && !this.pathMatches(page.path, includeUrls)) return false;
    if (excludeUrls.length && this.pathMatches(page.path, excludeUrls)) return false;

    if (productHandles.length && !this.arrayIncludesNormalized(productHandles, page.productHandle)) return false;
    if (excludeProductHandles.length && this.arrayIncludesNormalized(excludeProductHandles, page.productHandle)) return false;

    if (collectionHandles.length && !this.arrayIncludesNormalized(collectionHandles, page.collectionHandle)) return false;
    if (excludeCollectionHandles.length && this.arrayIncludesNormalized(excludeCollectionHandles, page.collectionHandle)) return false;

    if (vendors.length && !this.arrayIncludesNormalized(vendors, page.productVendor)) return false;
    if (productTypes.length && !this.arrayIncludesNormalized(productTypes, page.productType)) return false;
    if (templates.length && !this.arrayIncludesNormalized(templates, page.template)) return false;

    if (vis.tags && !this.anyTagMatch(vis.tags, page.productTags || [])) return false;

    return true;
  }

  getEligiblePopups() {
    const all = (this.data.popups || []).filter(Boolean);

    return all
      .filter((popup) => popup.enabled)
      .filter((popup) => this.scheduleMatches(popup.schedule || {}))
      .filter((popup) => this.deviceMatches(popup.audience?.deviceTarget || 'all'))
      .filter((popup) => this.triggerAudienceMatches(popup.trigger || {}, popup.audience || {}))
      .filter((popup) => this.visibilityMatches(popup))
      .filter((popup) => this.shouldRespectFrequency(popup))
      .sort((a, b) => (a.priority || 100) - (b.priority || 100));
  }

  initTriggers() {
    const uniqueModes = [...new Set(this.eligible.map((popup) => popup.trigger?.mode || 'page-load'))];
    uniqueModes.forEach((mode) => {
      if (mode === 'page-load') this.handlePageLoadTriggers();
      if (mode === 'delay') this.handleDelayTriggers();
      if (mode === 'scroll') this.handleScrollTriggers();
      if (mode === 'exit-intent') this.handleExitIntentTriggers();
      if (mode === 'click') this.handleClickTriggers();
      if (mode === 'manual') this.handleManualTriggers();
    });

    this.handleAddToCartTriggers();
  }

  getFirstEligibleByTrigger(mode, filterFn = null) {
    const list = this.eligible.filter((p) => (p.trigger?.mode || 'page-load') === mode);
    const filtered = filterFn ? list.filter(filterFn) : list;
    return filtered[0] || null;
  }

  requestOpen(popup) {
    if (!popup) return;
    if (this.activePopup && !this.data.allowMultiple) {
      if (!this.queue.find((x) => x.id === popup.id)) this.queue.push(popup);
      return;
    }

    if (!this.activePopup) {
      this.openPopup(popup);
      return;
    }

    if (this.data.allowMultiple) {
      this.openPopup(popup);
    }
  }

  handlePageLoadTriggers() {
    const popup = this.getFirstEligibleByTrigger('page-load');
    if (!popup) return;
    requestAnimationFrame(() => this.requestOpen(popup));
  }

  handleDelayTriggers() {
    const popup = this.getFirstEligibleByTrigger('delay');
    if (!popup) return;
    const delay = (parseInt(popup.trigger?.delaySeconds || 0, 10) || 0) * 1000;
    const timer = setTimeout(() => this.requestOpen(popup), delay);
    this.triggerListeners.push(() => clearTimeout(timer));
  }

  handleScrollTriggers() {
    const popup = this.getFirstEligibleByTrigger('scroll');
    if (!popup) return;

    const threshold = parseInt(popup.trigger?.scrollPercent || 35, 10) || 35;
    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const percent = max > 0 ? (scrollTop / max) * 100 : 100;
      if (percent >= threshold) {
        this.requestOpen(popup);
        window.removeEventListener('scroll', onScroll);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    this.triggerListeners.push(() => window.removeEventListener('scroll', onScroll));
  }

  handleExitIntentTriggers() {
    const popup = this.getFirstEligibleByTrigger('exit-intent');
    if (!popup || this.hasExitIntentBound) return;

    const onMouseOut = (e) => {
      if (e.clientY <= 6) {
        this.requestOpen(popup);
        document.removeEventListener('mouseout', onMouseOut);
      }
    };

    document.addEventListener('mouseout', onMouseOut);
    this.hasExitIntentBound = true;
    this.triggerListeners.push(() => document.removeEventListener('mouseout', onMouseOut));
  }

  handleClickTriggers() {
    const clickPopups = this.eligible.filter((p) => (p.trigger?.mode || '') === 'click');
    clickPopups.forEach((popup) => {
      const selector = popup.trigger?.clickSelector;
      if (!selector) return;
      const handler = (e) => {
        const target = e.target.closest(selector);
        if (!target) return;
        e.preventDefault();
        this.requestOpen(popup);
      };
      document.addEventListener('click', handler);
      this.triggerListeners.push(() => document.removeEventListener('click', handler));
    });
  }

  handleManualTriggers() {
    document.addEventListener('popup-builder:open', (event) => {
      const name = event.detail?.name;
      if (!name) return;
      const popup = this.eligible.find((p) => this.normalize(p.trigger?.manualName) === this.normalize(name));
      if (popup) this.requestOpen(popup);
    });
  }

  handleAddToCartTriggers() {
    const popup = this.eligible.find((p) => p.trigger?.afterAddToCart);
    if (!popup) return;

    document.addEventListener('cart:item-added', () => this.requestOpen(popup));
    document.addEventListener('ajaxProduct:added', () => this.requestOpen(popup));
    document.addEventListener('product:added', () => this.requestOpen(popup));
  }

  buildBadges(content) {
    const badges = [content.badge1, content.badge2, content.badge3].filter(Boolean);
    if (!badges.length) return '';
    return `<div class="popup-builder-badges">${badges.map((b) => `<span class="popup-builder-badge">${b}</span>`).join('')}</div>`;
  }

  buildCountdown(content, popupId) {
    if (content.countdownMode !== 'fixed' || !content.countdownEnd) return '';
    return `
      <div class="popup-builder-countdown" data-countdown="${popupId}" data-end="${content.countdownEnd}">
        <div class="popup-builder-countdown-item"><span class="popup-builder-countdown-num" data-unit="days">00</span><span class="popup-builder-countdown-label">D</span></div>
        <div class="popup-builder-countdown-item"><span class="popup-builder-countdown-num" data-unit="hours">00</span><span class="popup-builder-countdown-label">H</span></div>
        <div class="popup-builder-countdown-item"><span class="popup-builder-countdown-num" data-unit="minutes">00</span><span class="popup-builder-countdown-label">Min</span></div>
        <div class="popup-builder-countdown-item"><span class="popup-builder-countdown-num" data-unit="seconds">00</span><span class="popup-builder-countdown-label">Sec</span></div>
      </div>
    `;
  }

  buildMedia(popup) {
    const media = popup.media || {};
    if (media.mediaPosition === 'none') return '';

    let inner = '';
    if (media.videoUrl) {
      inner = `<video src="${media.videoUrl}" autoplay muted loop playsinline></video>`;
    } else if (media.image) {
      inner = `<img src="${media.image}" alt="${this.escapeAttr(media.imageAlt || popup.content.heading || '')}" loading="lazy" width="1200" height="800">`;
    }

    if (!inner) return '';
    return `<div class="popup-builder-media" data-ratio="${media.mediaRatio || 'landscape'}">${inner}</div>`;
  }

  buildButtons(popup) {
    const c = popup.content || {};
    const buttons = [];

    if (c.buttonLabel) {
      buttons.push(
        `<a href="${c.buttonUrl || '#'}" class="popup-builder-btn popup-builder-btn--primary" data-action="${c.buttonAction || 'link'}">${c.buttonLabel}</a>`
      );
    }

    if (c.secondaryButtonLabel) {
      buttons.push(
        `<a href="${c.secondaryButtonUrl || '#'}" class="popup-builder-btn popup-builder-btn--secondary" data-action="${c.secondaryButtonAction || 'close'}">${c.secondaryButtonLabel}</a>`
      );
    }

    return buttons.length ? `<div class="popup-builder-actions">${buttons.join('')}</div>` : '';
  }

  buildPopupMarkup(popup) {
    const c = popup.content || {};
    const mediaHtml = this.buildMedia(popup);
    const dividerHtml = c.showDivider ? `<div class="popup-builder-divider"></div>` : '';
    const badgesHtml = this.buildBadges(c);
    const countdownHtml = this.buildCountdown(c, popup.id);
    const customHtml = c.customHtml ? `<div class="popup-builder-custom-html">${c.customHtml}</div>` : '';

    return `
      <div class="popup-builder-layer" data-popup-id="${popup.id}" style="
        --pb-z:${parseInt(popup.design?.zIndex || 500, 10)};
        --pb-bg:${popup.design?.backgroundColor || '#fff'};
        --pb-text:${popup.design?.textColor || '#111'};
        --pb-muted:${popup.design?.mutedTextColor || '#666'};
        --pb-accent:${popup.design?.accentColor || '#111'};
        --pb-border:${popup.design?.borderColor || '#e5e5e5'};
        --pb-border-width:${popup.design?.borderWidth || 1}px;
        --pb-radius:${popup.design?.radius || 20}px;
        --pb-shadow:${popup.design?.shadow || 40};
        --pb-duration:${popup.animation?.duration || 320}ms;
        --pb-overlay-opacity:${((popup.design?.overlayOpacity || 40) / 100).toFixed(2)};
        --pb-overlay-blur:${popup.design?.backdropBlur || 8}px;
        --pb-width:${popup.design?.widthDesktop || 620}px;
        --pb-width-tablet:${popup.design?.widthTablet || 560}px;
        --pb-width-mobile:${popup.design?.widthMobile || 360}px;
        --pb-max-width:${popup.design?.maxWidthDesktop || 720}px;
        --pb-max-width-tablet:${popup.design?.maxWidthTablet || 620}px;
        --pb-max-width-mobile:${popup.design?.maxWidthMobile || 380}px;
        --pb-height:${popup.design?.heightDesktop > 0 ? popup.design.heightDesktop + 'px' : 'auto'};
        --pb-height-tablet:${popup.design?.heightTablet > 0 ? popup.design.heightTablet + 'px' : 'auto'};
        --pb-height-mobile:${popup.design?.heightMobile > 0 ? popup.design.heightMobile + 'px' : 'auto'};
        --pb-max-height:${popup.design?.maxHeightDesktop || 760}px;
        --pb-max-height-tablet:${popup.design?.maxHeightTablet || 680}px;
        --pb-max-height-mobile:${popup.design?.maxHeightMobile || 640}px;;
        --pb-padding:${popup.design?.paddingDesktop || 28}px;
        --pb-padding-tablet:${popup.design?.paddingTablet || 24}px;
        --pb-padding-mobile:${popup.design?.paddingMobile || 20}px;
        --pb-gap:${popup.design?.gapDesktop || 18}px;
        --pb-gap-tablet:${popup.design?.gapTablet || 16}px;
        --pb-gap-mobile:${popup.design?.gapMobile || 14}px;
      ">
        <div class="popup-builder-overlay ${popup.design?.blurOverlay ? 'is-blur' : ''}" style="background:${this.hexToRgba(popup.design?.overlayColor || '#000000', (popup.design?.overlayOpacity || 40) / 100)}"></div>
        <div class="popup-builder-stage" data-position="${this.getPositionForDevice(popup)}">
          <div class="popup-builder-dialog" role="dialog" aria-modal="true" aria-labelledby="pb-title-${popup.id}" data-animation="${popup.animation?.type || 'scale'}">
            <div class="popup-builder-shell" data-layout="${popup.design?.layoutStyle || 'classic'}" ${popup.media?.backgroundImage && popup.media?.mediaPosition === 'background' ? `style="background-image:url('${popup.media.backgroundImage}')"` : ''}>
              ${popup.close?.showCloseButton ? `
                <button type="button" class="popup-builder-close" aria-label="Close popup">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                </button>
              ` : ''}

              ${popup.media?.mediaPosition === 'top' || popup.media?.mediaPosition === 'left' ? mediaHtml : ''}

              <div class="popup-builder-content">
                ${c.eyebrow ? `<p class="popup-builder-eyebrow">${c.eyebrow}</p>` : ''}
                ${c.heading ? `<h2 id="pb-title-${popup.id}" class="popup-builder-heading">${c.heading}</h2>` : ''}
                ${c.subheading ? `<p class="popup-builder-subheading">${c.subheading}</p>` : ''}
                ${badgesHtml}
                ${dividerHtml}
                ${countdownHtml}
                ${c.richtext ? `<div class="popup-builder-richtext">${c.richtext}</div>` : ''}
                ${customHtml}
                ${this.buildButtons(popup)}
              </div>

              ${popup.media?.mediaPosition === 'right' ? mediaHtml : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  openPopup(popup) {
    if (!popup) return;
    if (popup.frequency?.mode === 'session' && this.sessionSeen.has(popup.id)) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = this.buildPopupMarkup(popup);
    const layer = wrapper.firstElementChild;
    if (!layer) return;

    this.mount.appendChild(layer);
    this.activePopup = popup;
    this.activeLayer = layer;
    this.sessionSeen.add(popup.id);
    this.incrementViews(popup);
    if (popup.close?.lockPageScroll) {
    document.documentElement.classList.add('popup-builder-lock');
    document.body.classList.add('popup-builder-lock');
    }
    const dialog = layer.querySelector('.popup-builder-dialog');
    const overlay = layer.querySelector('.popup-builder-overlay');
    const closeBtn = layer.querySelector('.popup-builder-close');

    requestAnimationFrame(() => layer.classList.add('is-active'));

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup(popup, 'close-button'));
    }

    if (overlay && popup.close?.closeOnOverlay) {
      overlay.addEventListener('click', () => this.closePopup(popup, 'overlay'));
    }

    dialog.querySelectorAll('.popup-builder-btn').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        const action = btn.getAttribute('data-action');
        if (action === 'close') {
          event.preventDefault();
          this.closePopup(popup, 'button');
          return;
        }
        if (popup.close?.closeAfterButtonClick) {
          this.closePopup(popup, 'button-link');
        }
      });
    });

    if (popup.close?.closeOnEsc) {
      this.escHandler = (event) => {
        if (event.key === 'Escape') this.closePopup(popup, 'esc');
      };
      document.addEventListener('keydown', this.escHandler);
    }

    if (popup.close?.autoClose) {
      this.autoCloseTimer = setTimeout(() => {
        this.closePopup(popup, 'auto');
      }, (parseInt(popup.close?.autoCloseSeconds || 8, 10) || 8) * 1000);
    }

    this.initCountdown(layer, popup);
    setTimeout(() => {
      dialog.setAttribute('tabindex', '-1');
      dialog.focus();
    }, 30);
  }

  closePopup(popup, reason) {
    if (!this.activeLayer) return;

    if (popup.frequency?.rememberClosed) this.setClosedAt(popup);
    if (popup.frequency?.closeForever) this.setClosedForever(popup);

    this.activeLayer.classList.remove('is-active');

    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }

    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    const currentLayer = this.activeLayer;
    setTimeout(() => {
      currentLayer.remove();
      if (this.activeLayer === currentLayer) {
        this.activeLayer = null;
        this.activePopup = null;
      }

      if (!document.querySelector('.popup-builder-layer.is-active')) {
      document.documentElement.classList.remove('popup-builder-lock');
      document.body.classList.remove('popup-builder-lock');
      }

      if (popup.behavior?.allowQueueNext !== false) {
        this.openNextEligible();
      }
    }, popup.animation?.duration || 320);

    this.log('Closed popup', popup.id, reason);
  }

  openNextEligible() {
    if (this.activePopup && !this.data.allowMultiple) return;

    const remaining = this.eligible.filter((p) => p.id !== this.activePopup?.id && this.shouldRespectFrequency(p));
    const next = remaining[0];
    if (next) {
      setTimeout(() => this.requestOpen(next), 120);
    }
  }

  initCountdown(layer, popup) {
    const countdown = layer.querySelector(`[data-countdown="${popup.id}"]`);
    if (!countdown) return;

    const end = new Date(countdown.getAttribute('data-end'));
    if (Number.isNaN(end.getTime())) return;

    const tick = () => {
      const diff = end.getTime() - Date.now();
      if (diff <= 0) {
        countdown.remove();
        clearInterval(timer);
        return;
      }

      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      countdown.querySelector('[data-unit="days"]').textContent = String(days).padStart(2, '0');
      countdown.querySelector('[data-unit="hours"]').textContent = String(hours).padStart(2, '0');
      countdown.querySelector('[data-unit="minutes"]').textContent = String(minutes).padStart(2, '0');
      countdown.querySelector('[data-unit="seconds"]').textContent = String(seconds).padStart(2, '0');
    };

    tick();
    const timer = setInterval(tick, 1000);
  }

  getPositionForDevice(popup) {
    const device = this.getDeviceType();
    if (device === 'mobile') return popup.design?.positionMobile || 'bottom-center';
    if (device === 'tablet') return popup.design?.positionTablet || 'center';
    return popup.design?.positionDesktop || 'center';
  }

  escapeAttr(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  hexToRgba(hex, opacity) {
    const h = String(hex || '#000000').replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const bigint = parseInt(full, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
}

if (!customElements.get('popup-builder')) {
  customElements.define('popup-builder', PopupBuilder);
}

window.PopupBuilderAPI = {
  open(name) {
    document.dispatchEvent(new CustomEvent('popup-builder:open', { detail: { name } }));
  }
};